import { useEffect } from "react";
import { Link } from "react-router-dom";
import { CheckCircle2, ArrowRight, Star, MapPin, Phone } from "lucide-react";
import { WHATSAPP_LINK, CITY } from "@/lib/constants";
import { useSEO } from "@/lib/useSEO";

export const LANDING_PAGES = {
  "thesis-printing-lucknow": {
    h1: "Thesis Printing & Binding in Lucknow",
    eyebrow: "Lucknow · Same-day proof",
    subtitle: "Hard-bound, gold-embossed thesis printing delivered anywhere in Lucknow — from Aliganj to Gomti Nagar.",
    metaTitle: "Thesis Printing Lucknow · Hard Binding · Gold Embossing · ScholarPrintHub",
    metaDescription: "Get your thesis printed and hard-bound in Lucknow. Golden embossing, spine printing, premium paper, and doorstep delivery across Lucknow. Live cost calculator available.",
    keywords: ["Thesis Printing Lucknow", "Thesis Binding Lucknow", "Hard Binding Lucknow", "Dissertation Print Lucknow", "PhD Thesis Print Lucknow"],
    benefits: [
      "Local Lucknow delivery in 24–48 hours",
      "Hard binding with navy or leather cover",
      "Golden or silver embossing for author, title & university",
      "Premium 80–100 GSM paper, sharp black-and-white printing",
      "Proof approval on WhatsApp before we go to print",
      "Pay-on-delivery available for Lucknow orders",
    ],
    cities: ["Gomti Nagar", "Hazratganj", "Aliganj", "Aminabad", "Indira Nagar", "Charbagh", "Aashiana", "Vikas Nagar", "Chinhat", "Rajajipuram"],
    faq: [
      ["How much does thesis printing cost in Lucknow?", "For a typical 150-page thesis with hard binding and golden embossing, the estimate ranges ₹500–₹850 per copy. Use our live calculator for a precise number."],
      ["Do you deliver same-day in Lucknow?", "Same-day delivery is possible for orders confirmed before 12 noon, subject to availability. Standard Lucknow delivery is 24–48 hours."],
      ["Can I get 3 copies with different colored covers?", "Yes. Just specify the cover colour and embossing preferences for each copy in the upload form."],
    ],
    primaryCta: { to: "/upload", label: "Upload PDF for Printing" },
    secondaryCta: { to: "/calculator", label: "Check Cost" },
  },
  "hard-binding-online-india": {
    h1: "Hard Binding Online — Delivered Anywhere in India",
    eyebrow: "Pan-India delivery",
    subtitle: "Send us your PDF, we'll hard-bind it with gold embossing and courier it to your doorstep — Kashmir to Kanyakumari.",
    metaTitle: "Hard Binding Online India · Dissertation & Thesis Print · ScholarPrintHub",
    metaDescription: "Order hard binding online in India. Navy or leather covers, golden embossing, spine printing, and doorstep courier delivery. Live cost calculator, secure PDF upload.",
    keywords: ["Hard Binding Online India", "Dissertation Binding India", "Thesis Binding Online", "Leather Binding Online India", "Golden Embossing Online"],
    benefits: [
      "Nationwide courier — track your order end-to-end",
      "Choice of hard, soft, leather, and spiral binding",
      "Golden or silver embossing on cover and spine",
      "Coloured or B&W printing on premium paper",
      "Secure encrypted PDF upload (files never shared)",
      "Bulk discounts for departments and universities",
    ],
    cities: ["Delhi NCR", "Mumbai", "Bengaluru", "Hyderabad", "Chennai", "Kolkata", "Pune", "Ahmedabad", "Jaipur", "Chandigarh", "Bhopal", "Patna", "Kochi"],
    faq: [
      ["Do you ship hard-bound thesis to Bengaluru / Delhi / Mumbai?", "Yes. Standard India delivery is 3–5 working days; express is 1–2 working days depending on pincode."],
      ["Is the file secure while being printed?", "All uploads are stored in encrypted cloud storage, accessed only by our team for the print run, and can be deleted after delivery on request."],
      ["Can I pay after receiving proof?", "Yes. You approve a digital proof over WhatsApp/email; payment happens once you approve, before we go to press."],
    ],
    primaryCta: { to: "/upload", label: "Upload PDF for Printing" },
    secondaryCta: { to: "/calculator", label: "Check Cost" },
  },
  "thesis-writing-assistance-india": {
    h1: "Thesis Writing Assistance for Indian Scholars",
    eyebrow: "Chapter-wise support",
    subtitle: "Editing, formatting, methodology support, literature review, references — for Ph.D., M.Sc., MBBS, MBA, LL.M., nursing and law scholars.",
    metaTitle: "Thesis Writing Assistance India · APA · Vancouver · IEEE · ScholarPrintHub",
    metaDescription: "Get chapter-wise thesis writing assistance in India. Literature review, methodology, results, references, plagiarism check, and formatting — for Ph.D. and postgraduate scholars.",
    keywords: ["Thesis Writing Assistance India", "PhD Thesis Support India", "Dissertation Help India", "Thesis Editing India", "Thesis Formatting India"],
    benefits: [
      "APA, MLA, IEEE, Vancouver, Chicago, Bluebook citations",
      "Methodology and sample size explanation",
      "Structured literature review with proper citations",
      "Results, discussion, and conclusion editing",
      "Plagiarism check with ethical paraphrasing",
      "Confidential handling — files never shared with third parties",
    ],
    cities: ["Delhi", "Mumbai", "Bengaluru", "Chennai", "Kolkata", "Hyderabad", "Lucknow", "Chandigarh", "Pune"],
    faq: [
      ["Do you 'write' the thesis for me?", "No. Our services are academic support — editing, formatting, citation correction, methodology guidance, and plagiarism review. Ownership remains with the student."],
      ["Which citation styles do you handle?", "APA, MLA, IEEE, Vancouver, Chicago, Bluebook, and any custom university format you share the guidelines for."],
      ["How long does a chapter-level edit take?", "Turnaround depends on chapter length; most 30–50 page chapters are returned within 3–5 working days."],
    ],
    primaryCta: { to: "/academic-request", label: "Request Academic Help" },
    secondaryCta: { to: "/services/thesis-writing", label: "See what's included" },
  },
  "synopsis-writing-service": {
    h1: "Synopsis Writing Service for Ph.D. & Master's Scholars",
    eyebrow: "Ph.D. · Nursing · Law · Medical · Management",
    subtitle: "Research proposals, objectives, hypothesis framing, methodology, sample size, and references — structured to university format.",
    metaTitle: "Synopsis Writing Service India · Ph.D. · Nursing · Medical · ScholarPrintHub",
    metaDescription: "Synopsis writing service for Ph.D., M.Sc., nursing, medical, law and management scholars. Objectives, hypothesis, methodology, references — university-format ready.",
    keywords: ["Synopsis Writing Service", "PhD Synopsis Help", "Nursing Synopsis", "Medical Synopsis", "Law Synopsis"],
    benefits: [
      "Topic refinement with feasibility check",
      "Objectives and hypothesis framing",
      "Structured methodology with sample size explanation",
      "University-format ready (guidelines followed)",
      "References formatted in APA / Vancouver / IEEE",
      "Final Word + PDF deliverables",
    ],
    cities: ["Delhi", "Mumbai", "Bengaluru", "Chennai", "Lucknow", "Kolkata", "Hyderabad", "Pune"],
    faq: [
      ["How many revisions are included?", "Two structured revision rounds are included per synopsis. Additional revisions can be quoted separately."],
      ["Do you handle nursing/medical synopsis?", "Yes. We regularly work with B.Sc/M.Sc. Nursing, MDS, MBBS, and MD scholars — respecting the department's specific format."],
      ["Can I get it printed after approval?", "Yes. Once you approve the final synopsis, order printing and binding directly on the same platform."],
    ],
    primaryCta: { to: "/academic-request", label: "Request Synopsis Support" },
    secondaryCta: { to: "/services/synopsis-writing", label: "See what's included" },
  },
};

export default function LandingPage({ slug }) {
  const page = LANDING_PAGES[slug];

  useSEO(page ? {
    title: page.metaTitle,
    description: page.metaDescription,
    keywords: page.keywords.join(", "),
    jsonLd: [
      {
        "@context": "https://schema.org",
        "@type": "Service",
        "name": page.h1,
        "description": page.metaDescription,
        "provider": { "@type": "LocalBusiness", "name": "ScholarPrintHub" },
        "areaServed": page.cities.map((c) => ({ "@type": "City", "name": c })),
      },
      {
        "@context": "https://schema.org",
        "@type": "FAQPage",
        "mainEntity": page.faq.map(([q, a]) => ({
          "@type": "Question",
          "name": q,
          "acceptedAnswer": { "@type": "Answer", "text": a },
        })),
      },
    ],
  } : { title: undefined });

  useEffect(() => {
    if (!page) return;
    const kw = ensureMeta("keywords");
    kw.setAttribute("content", page.keywords.join(", "));
  }, [page]);

  if (!page) {
    return (
      <div className="container-app py-20">
        <h1 className="font-display text-4xl text-navy">Landing page not found</h1>
      </div>
    );
  }

  return (
    <div data-testid={`landing-${slug}`}>
      {/* HERO */}
      <section className="border-b border-slate-200 bg-white">
        <div className="container-app py-20 grid lg:grid-cols-12 gap-10">
          <div className="lg:col-span-8">
            <span className="section-eyebrow">{page.eyebrow}</span>
            <div className="divider-gold mt-2 mb-4" />
            <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl leading-[1.05] text-navy">{page.h1}</h1>
            <p className="mt-6 text-slate-600 text-lg max-w-2xl">{page.subtitle}</p>
            <div className="mt-8 flex flex-wrap gap-3">
              <Link to={page.primaryCta.to} className="btn-gold" data-testid="lp-primary-cta">{page.primaryCta.label} <ArrowRight className="w-4 h-4" /></Link>
              <Link to={page.secondaryCta.to} className="btn-outline" data-testid="lp-secondary-cta">{page.secondaryCta.label}</Link>
              <a href={WHATSAPP_LINK} target="_blank" rel="noreferrer" className="btn-primary"><Phone className="w-4 h-4" /> WhatsApp</a>
            </div>
          </div>
          <aside className="lg:col-span-4 border border-slate-200 rounded-sm p-6 bg-slate-50 self-start">
            <div className="section-eyebrow">Why {CITY.split(",")[0]} scholars trust us</div>
            <ul className="mt-4 space-y-2 text-sm text-slate-700">
              {[
                "Owner-operated by Suraj Dubey",
                "Transparent per-page & per-binding pricing",
                "Files handled confidentially",
                "500+ scholars supported since 2020",
              ].map((t, i) => (
                <li key={i} className="flex gap-2"><Star className="w-4 h-4 text-gold shrink-0 mt-0.5" /><span>{t}</span></li>
              ))}
            </ul>
          </aside>
        </div>
      </section>

      {/* BENEFITS */}
      <section className="container-app py-20">
        <div className="grid lg:grid-cols-12 gap-10">
          <div className="lg:col-span-5">
            <span className="section-eyebrow">What you get</span>
            <div className="divider-gold mt-2 mb-4" />
            <h2 className="font-display text-4xl text-navy">Everything included, nothing to source separately.</h2>
          </div>
          <ul className="lg:col-span-7 grid sm:grid-cols-2 gap-3">
            {page.benefits.map((b, i) => (
              <li key={i} className="flex gap-2 text-sm text-slate-700 border border-slate-200 rounded-sm p-4">
                <CheckCircle2 className="w-4 h-4 text-gold shrink-0 mt-0.5" /><span>{b}</span>
              </li>
            ))}
          </ul>
        </div>
      </section>

      {/* CITIES / AREAS */}
      <section className="bg-slate-50 border-y border-slate-200 py-14">
        <div className="container-app">
          <span className="section-eyebrow">Serving these areas</span>
          <div className="divider-gold mt-2 mb-6" />
          <div className="flex flex-wrap gap-2">
            {page.cities.map((c) => (
              <span key={c} className="inline-flex items-center gap-1 text-xs border border-slate-200 rounded-sm px-3 py-1 bg-white text-slate-700">
                <MapPin className="w-3 h-3 text-royal" /> {c}
              </span>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="container-app py-20">
        <span className="section-eyebrow">FAQ</span>
        <div className="divider-gold mt-2 mb-6" />
        <div className="grid lg:grid-cols-2 gap-6">
          {page.faq.map(([q, a], i) => (
            <div key={i} className="border border-slate-200 rounded-sm p-5">
              <h3 className="font-display text-xl text-navy">{q}</h3>
              <p className="text-sm text-slate-600 mt-2">{a}</p>
            </div>
          ))}
        </div>
      </section>

      {/* CTA */}
      <section className="container-app pb-24">
        <div className="border border-slate-200 rounded-sm bg-navy text-white p-10 grid lg:grid-cols-12 gap-6 items-center">
          <div className="lg:col-span-8">
            <span className="section-eyebrow text-gold">Ready to start?</span>
            <h3 className="font-display text-3xl sm:text-4xl mt-2">{page.h1}</h3>
          </div>
          <div className="lg:col-span-4 flex flex-wrap gap-3">
            <Link to={page.primaryCta.to} className="btn-gold">{page.primaryCta.label}</Link>
            <a href={WHATSAPP_LINK} target="_blank" rel="noreferrer" className="btn-outline border-white text-white hover:bg-white hover:text-navy">WhatsApp</a>
          </div>
        </div>
      </section>
    </div>
  );
}

function ensureMeta(name) {
  let el = document.querySelector(`meta[name="${name}"]`);
  if (!el) {
    el = document.createElement("meta");
    el.setAttribute("name", name);
    document.head.appendChild(el);
  }
  return el;
}
